import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type * as React from "react";

import { cn } from "@ando/ui";

import { useTakeoverBack } from "@/lib/takeoverOrigin";

/**
 * Settings parent route (Jun 13, 2026 rebuild — "tutto come integrations").
 *
 * Each ``/settings/*`` child renders full-screen — same chrome as
 * ``/integrations`` and ``/admin``. The avatar drawer (``UserMenuButton``)
 * is the single source of jump-navigation.
 *
 * BACK AFFORDANCE (Jul 27, 2026): the settings surfaces render their OWN
 * back bar HERE, in-flow above the child ``<Outlet>``. The ModalShell's
 * top bar kept not showing on these routes for the user, so — exactly like
 * the console, which paints its own back nav — the settings sub-shell now
 * owns it, and ``_modal/route.tsx`` skips its bar for ``/settings/**`` so
 * there is never a double bar. This is a plain in-flow element (not a
 * ``fixed`` overlay), so it is impossible to hide behind stacking contexts.
 *
 * BOXED (Aug 25, 2026): back control + ``<Outlet>`` share one centred
 * ``max-w-6xl`` column, matching ``/integrations``. The settings pages have
 * no measure of their own — header and form both ran flush to the window
 * edge, so on a wide viewport the whole surface hung off the left with a
 * void to the right. Capping HERE boxes all 15 children at once instead of
 * threading a max-width through each one.
 */
export const Route = createFileRoute("/_authed/_modal/settings")({
  component: SettingsShell,
});

function SettingsShell(): React.ReactElement {
  const { i18n } = useLingui();
  const back = useTakeoverBack();

  return (
    <div className="flex h-full flex-col bg-cream">
      {/* Back control — light on purpose (no pill, no divider) and optically
          aligned with the surface label below via ``-ml-2``. Plain in-flow
          element, never a ``fixed`` overlay: that is what made the shared bar
          invisible here in the first place. */}
      <div className="shrink-0 pt-3">
        <div className="mx-auto w-full max-w-6xl px-8">
          <button
            type="button"
            onClick={back}
            className={cn(
              "group -ml-2 inline-flex h-8 items-center gap-1.5 rounded-lg px-2 font-medium text-[12px] text-ink-2 transition-colors hover:text-ink-1",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/70 focus-visible:ring-offset-1 focus-visible:ring-offset-cream",
            )}
            aria-label={i18n._(msg`Torna al workspace`)}
          >
            <ArrowLeft
              size={14}
              strokeWidth={2}
              aria-hidden
              className="group-hover:-translate-x-0.5 text-ink-3 transition-transform duration-150 group-hover:text-ink-1 motion-reduce:transform-none"
            />
            <span>{i18n._(msg`Torna al workspace`)}</span>
          </button>
        </div>
      </div>
      <div className="mx-auto min-h-0 w-full max-w-6xl flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}
